/**
 * Chip / pill colors for notice feed + creation live preview.
 * Keeps dashboard preview aligned with palette.template-* (same tokens as TemplateCard /
 * native app filter styling). Update theme.ts template* values when the app palette changes.
 */
import type { Theme } from "@mui/material/styles";
import { lightTheme } from "../styles/theme";
import type { Subcircle } from "../hooks/useSubcircles";
import { parseSubcircleName } from "./subcircleUtils";
import { isDriveVideoFile } from "./driveFolder";

/** Types persisted on notices / passed into feed cards. */
export type NoticeStoredType =
  | "info"
  | "urgent"
  | "event"
  | "image"
  | "image_notice"
  | "direct"
  | "poll";

/** Chip display type (includes display-only "video" from attachments). */
export type NoticeFeedCardType =
  | NoticeStoredType
  /** Display-only chip when attachments are video (not a stored notice type). */
  | "video";

export type NoticeFeedAttachment = {
  type?: string;
  name?: string;
  url?: string;
};

const isVideoAttachment = (att: NoticeFeedAttachment): boolean =>
  isDriveVideoFile(att.type, att.name ?? att.url);

const isImageAttachment = (att: NoticeFeedAttachment): boolean => {
  if (att.type?.startsWith("image/")) return true;
  const ref = (att.name ?? att.url ?? "").toLowerCase();
  return /\.(jpe?g|png|gif|webp|heic|bmp|svg)$/.test(ref);
};

/**
 * Primary type chip: prefer attachment media when notice type is image/image_notice
 * but files are video-only (common when publishing via the image-notice flow).
 */
export function resolveNoticeFeedTypeChip(
  noticeType: NoticeStoredType,
  attachments?: NoticeFeedAttachment[] | null
): NoticeFeedCardType {
  const normalizedType: NoticeFeedCardType =
    noticeType === "image_notice" ? "image" : noticeType;

  const atts = (attachments ?? []).filter((a) => a?.url || a?.name);
  if (atts.length === 0) {
    return normalizedType;
  }

  const allVideo = atts.every(isVideoAttachment);
  if (allVideo) {
    return "video";
  }

  const hasVideo = atts.some(isVideoAttachment);
  const hasImage = atts.some(isImageAttachment);
  if (
    (noticeType === "image" || noticeType === "image_notice") &&
    hasVideo &&
    !hasImage
  ) {
    return "video";
  }

  return normalizedType;
}

export function resolveNoticeAudienceCircleLabel(
  targetSubcircle: string | undefined,
  subcircles: Subcircle[] | undefined,
  schoolId?: string,
  schoolName?: string
): string {
  if (!targetSubcircle) {
    return schoolName || "All Students";
  }

  if (targetSubcircle === schoolId) {
    return schoolName || "All Students";
  }

  if (subcircles && subcircles.length > 0) {
    const subcircle = subcircles.find((sc) => sc.circleId === targetSubcircle);
    if (subcircle?.name) {
      return subcircle.name;
    }
  }

  return parseSubcircleName(targetSubcircle);
}

/** Circle / audience chips — hash into theme avatar palette (matches feed). */
export function getAudienceCircleChipBackground(
  theme: Theme,
  circleName: string
): string {
  const palette =
    theme.palette.colors?.avatarColors ??
    lightTheme.colors.avatarColors;
  if (!palette?.length) {
    return lightTheme.colors.avatarColors[0];
  }
  const hash = circleName
    .split("")
    .reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return palette[hash % palette.length];
}

/**
 * Background for the primary “Info / Event / Image / Direct” chip (not the separate Urgent chip).
 */
export function getNoticeFeedTypeChipBackground(
  theme: Theme,
  type: NoticeFeedCardType
): string {
  const displayType = type === "image_notice" ? "image" : type;

  if (displayType === "urgent") {
    return theme.palette.template.urgentPill;
  }
  if (displayType === "event") {
    return theme.palette.template.eventPill;
  }
  if (displayType === "image") {
    return theme.palette.warning.main;
  }
  if (displayType === "video") {
    return theme.palette.primary.main;
  }
  if (displayType === "poll") {
    return theme.palette.primary.main;
  }

  return theme.palette.template.infoPill;
}

export function getNoticeFeedUrgentChipBackground(theme: Theme): string {
  return theme.palette.template.urgentPill;
}

/** Label on the primary type chip (same wording as NoticeCard). */
export function getNoticeFeedTypeChipLabel(type: NoticeFeedCardType): string {
  const displayType = type === "image_notice" ? "image" : type;
  if (displayType === "direct") {
    return "Direct";
  }
  if (displayType === "video") {
    return "Video";
  }
  if (displayType === "poll") {
    return "Poll";
  }
  return displayType.charAt(0).toUpperCase() + displayType.slice(1);
}

/** Secondary row chips (date, location, meta) — muted “filter” style (feed parity). */
export function getNoticeFeedMetaChipSx(theme: Theme): {
  bgcolor: string;
  color: string;
  border: string;
} {
  return {
    bgcolor: theme.palette.borders.slateLight,
    color: theme.palette.text.secondary,
    border: `1px solid ${theme.palette.borders.slate}`,
  };
}
